/* eslint-disable no-console */
import { FC, useEffect, useRef, useState } from 'react';
import { AppInitialProps, AppProps, NextWebVitalsMetric } from 'next/app';
import Script from 'next/script';
import { appWithTranslation } from 'next-i18next';
import { compose } from 'redux';
import { useRouter } from 'next/router';
import { useSelector, useDispatch } from 'react-redux';
import 'core-js/features/string/replace-all';
import 'core-js/features/array/find-last-index';
import { isAuthenticatedSelector, auth } from 'modules/users/duck';
import { wrapper } from 'src/store/index';
import { initialize as initializeSessionContext } from 'utils/session-context';
import {
  getSatoriHashCodeFromLocalStorage,
  getSatoriHashCodeFromApi,
  getUserIdFromLocalStorage,
  setFields as setGoogleAnalyticsFields,
} from 'utils/analytics';
import createReporter from 'utils/web-vitals-reporter';
import { getCookieByKey, AUTH_COOKIE, consumeAuthCookie } from '@z-components/utils/oauth';

import '../src/styles/styles.scss';
import { OPTIMIZE_TEST_ID_LIST } from 'utils/google-optimize-manager';
import { captureException } from '@sentry/nextjs';

if (process.env.ENABLE_MSW === 'true') {
  // eslint-disable-next-line @typescript-eslint/no-floating-promises
  import('../mocks/msw');
}

// Workaround for HubSpot chat SDK & TS typing
interface scopeWindow {
  HubSpotConversations?: {
    widget: {
      refresh: () => void;
    };
  };
  location: {
    href: string;
  };
}
declare let window: scopeWindow;

const WEB_VITALS_ENDPOINT_URL = `${process.env.API_HOST ?? ''}/api/events/web-vitals`;
const DEFAULT_SATORI_TRACKING_ID = '35a83868'; // This is default tracking for satori when user is not logged in

// Next.js provides generic getInitialProps when using class MyApp extends App which will be picked up by wrapper,
// so you must not extend App as you'll be opted out of Automatic Static Optimization:
// https://err.sh/next.js/opt-out-auto-static-optimization
// Just export a regular Functional Component as in the example above.
// Also workaround for https://github.com/vercel/next.js/issues/8592
const MyApp: FC<AppProps<AppInitialProps> & { err?: unknown }> = ({
  Component,
  err,
  pageProps,
}) => {
  const router = useRouter();
  const mounted = useRef(false);
  const dispatch = useDispatch();
  useEffect(() => {
    // would be required for any oauth flow that redirects back to z-web
    // with an auth token. dropped pro registration is a flow that consumes this logic
    const authCookie = getCookieByKey(document.cookie, AUTH_COOKIE);
    if (authCookie) {
      try {
        consumeAuthCookie({ url: window.location.href });
        dispatch(auth());
      } catch (error) {
        captureException(error);
      }
    }
  }, [dispatch]);

  useEffect(() => {
    initializeSessionContext(document.location.href, router.pathname.replace('/', ''));
  });

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  const [satoriHashCode, setSatoriHashCode] = useState(getSatoriHashCodeFromLocalStorage());

  const isAuthenticated = useSelector<boolean>(isAuthenticatedSelector);
  useEffect(() => {
    const userId = getUserIdFromLocalStorage();
    setGoogleAnalyticsFields({ userId });
    if (!satoriHashCode) {
      /* eslint-disable-next-line no-void */
      void getSatoriHashCodeFromApi()
        .then((hashCode) => {
          if (mounted.current) {
            setSatoriHashCode(hashCode ?? '');
          }
        })
        .catch(() => {
          /* do nothing when error occurred */
        });
    }
  }, [isAuthenticated]);

  const handleRouteEventHubspot = () => {
    if (window.HubSpotConversations) {
      window.HubSpotConversations.widget.refresh();
    }
  };
  useEffect(() => {
    router.events.on('routeChangeComplete', handleRouteEventHubspot);
    return () => {
      router.events.off('routeChangeComplete', handleRouteEventHubspot);
    };
  });

  /*
    Developer notes for Satori Hash Code
    The documentation on how to setup satori measurement tag for SPA
    https://satorihelp.zendesk.com/hc/ja/articles/360004121134-SPA-Single-Page-Application-%E3%82%92%E3%81%94%E5%88%A9%E7%94%A8%E3%81%AE%E3%82%B5%E3%82%A4%E3%83%88%E3%81%A7SATORI%E8%A8%88%E6%B8%AC%E3%82%BF%E3%82%B0%E3%82%92%E6%9C%89%E5%8A%B9%E3%81%AB%E3%81%99%E3%82%8B%E6%96%B9%E6%B3%95

    The above method does not work, so adding another method to use the stDmp variable as well
    https://satorihelp.zendesk.com/hc/ja/articles/360000473393-API%E3%83%90%E3%83%BC%E3%82%B8%E3%83%A7%E3%83%B34%E3%81%AE%E5%88%A9%E7%94%A8%E6%96%B9%E6%B3%95-%E3%82%AB%E3%82%B9%E3%82%BF%E3%83%9E%E3%83%BC%E6%96%B0%E8%A6%8F%E7%99%BB%E9%8C%B2-%E6%9B%B4%E6%96%B0-%E5%89%8A%E9%99%A4-
  */

  return (
    <>
      {process.env.GOOGLE_TAG_MANAGER_ID && (
        <Script
          strategy="afterInteractive"
          id="gtm-script"
          src={`https://www.googletagmanager.com/gtm.js?id=${process.env.GOOGLE_TAG_MANAGER_ID}`}
        />
      )}
      <Script strategy="afterInteractive" src="https://statics.a8.net/a8sales/a8sales.js" />
      <Script strategy="afterInteractive" src="https://statics.a8.net/a8sales/a8crossDomain.js" />
      {process.env.GOOGLE_RECAPTCHA_V3_KEY && (
        <>
          <Script
            strategy="afterInteractive"
            id="google-recpatcha_v3"
            src={`https://www.google.com/recaptcha/api.js?render=${process.env.GOOGLE_RECAPTCHA_V3_KEY}`}
          />
        </>
      )}
      {process.env.GOOGLE_OPTIMIZE_ID && OPTIMIZE_TEST_ID_LIST.length > 0 && (
        <>
          <Script
            strategy="afterInteractive"
            id="google-optimize-script"
            src={`https://www.googleoptimize.com/optimize.js?id=${process.env.GOOGLE_OPTIMIZE_ID}`}
          />
          <Script
            dangerouslySetInnerHTML={{
              __html:
                `(function(w,l){` +
                `w[l] = w[l] || [];w[l].push({'event':'optimize.activate'});` +
                `})(window,'dataLayer');`,
            }}
            id="google-optimize-activate"
          />
        </>
      )}
      {satoriHashCode && (
        <Script
          dangerouslySetInnerHTML={{
            __html: `var StDmp = {};
              StDmp.additionalParams = { c: "${satoriHashCode}-00" };`,
          }}
          id="satori-hash-code"
        />
      )}
      <Script
        strategy="afterInteractive"
        id="_-s-js-_"
        src={`//satori.segs.jp/s.js?c=${
          satoriHashCode ? `${satoriHashCode}-00` : DEFAULT_SATORI_TRACKING_ID
        }`}
      />
      {process.env.SATORI_WEB_PUSH_SERVER_ID && (
        <>
          <Script
            dangerouslySetInnerHTML={{
              __html: `_strwpcnf_=["https://webpush.satori.cloud","${process.env.SATORI_WEB_PUSH_SERVER_ID}"]`,
            }}
            id="satori-push-notification-setup"
          />
          <Script
            defer={true}
            strategy="afterInteractive"
            id="satori-push-notification-script"
            src={`https://webpush.satori.cloud/js/webpush.js`}
          />
        </>
      )}
      {process.env.NODE_ENV === 'production' && (
        <Script
          strategy="afterInteractive"
          id="hs-script-loader"
          async={true}
          defer={true}
          src="//js.hs-scripts.com/3926470.js"
        />
      )}
      {process.env.NODE_ENV === 'development' && (
        <Script
          strategy="afterInteractive"
          id="hs-script-loader"
          async={true}
          defer={true}
          src="//js.hs-scripts.com/21112044.js"
        />
      )}
      <Component {...pageProps} err={err} />
    </>
  );
};

export default compose(appWithTranslation, wrapper.withRedux)(MyApp);

const report = createReporter(WEB_VITALS_ENDPOINT_URL);

export function reportWebVitals(metric: NextWebVitalsMetric) {
  if (
    metric.label === 'web-vital' &&
    typeof window !== 'undefined' &&
    /^\/(home|lifestyle|learning|business|events)/.test(document.location.pathname)
  ) {
    report(metric);
  }
}
